import { Divider, Radio, Stack, Tooltip, Typography } from '@mui/material';
import { type SecurityScanResult, type Snapshot } from '@repo/api-gw-sdk';
import classnames from 'classnames';

import {
  getEvidenceStrings,
  hasSecurityIssue,
} from '@/components/security/securityUtils';
import { SkullIcon } from '@/components/security/skullIcon';
import { Icon } from '@/components/shared/icon';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import VaultTag from '@/components/vaults/vaultTag';
import type { BackupVault } from '@/data/vaults/backupVault';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, TIME_FORMAT } from '@/utils/dayjs';

interface SnapshotDetailsHeaderProps {
  snap: Snapshot;
  vault: BackupVault | undefined;
  isSelected: boolean;
  onSelect: () => void;
  snapshotSelection: boolean;
}

export const SnapshotDetailsHeader = ({
  snap,
  isSelected,
  onSelect,
  vault,
  snapshotSelection,
}: SnapshotDetailsHeaderProps) => {
  const date = isDateValid(snap.pointInTime)
    ? snap.pointInTime
    : snap.createdTime;
  const showPartial = snap.accessDeniedItemsCount;

  return (
    <Stack
      className={classnames('flex-1', { 'cursor-pointer': snapshotSelection })}
      direction='row'
      gap='40px'
      alignItems='center'
      onClick={() => onSelect()}
      sx={{
        '& .info-icon': {
          visibility: 'hidden',
        },
        '&:hover': {
          '& .info-icon': {
            visibility: showPartial ? 'visible' : 'hidden',
          },
        },
      }}
    >
      <Stack direction='row' gap='12px' alignItems='center'>
        {snapshotSelection && <Radio size='small' checked={isSelected} />}
        {vault && (
          <SnapshotIndicator
            sx={{
              margin: '6px',
            }}
            backgroundColor={vault.backgroundColor || null}
            isPartial={!!snap.accessDeniedItemsCount}
          />
        )}
        <Typography width='100px' variant='subtitle1'>
          {`${dayjs.utc(date).format(TIME_FORMAT)} UTC`}
        </Typography>
        <Stack width='102px' direction={'row'} gap='24px' alignItems='center'>
          <Tooltip
            title={
              snap.accessDeniedItemsCount === 1
                ? 'This is a partial snapshot. 1 file wasn`t backed up.'
                : `This is a partial snapshot. ${
                    snap.accessDeniedItemsCount
                  } files weren’t backed up.`
            }
            slotProps={{
              tooltip: {
                sx: {
                  padding: '16px',
                },
              },
            }}
          >
            <Icon
              iconClass='material-symbols-info-outline'
              className='info-icon'
            />
          </Tooltip>
          {hasSecurityIssue(snap) && (
            <>
              {showPartial ? (
                <Divider orientation='vertical' flexItem />
              ) : (
                // having a div, to keep the margin achieve by the stack gap
                <div />
              )}
              <RansomwareIndication snap={snap} />
            </>
          )}
        </Stack>
      </Stack>
      {vault && (
        <Stack direction='row' gap='12px' alignItems='center'>
          <Typography variant='subtitle1'>Stored in</Typography>
          <VaultTag vault={vault} />
        </Stack>
      )}
    </Stack>
  );
};

const RansomwareIndication = ({ snap }: { snap: Snapshot }) => {
  const evidences: SecurityScanResult[] = snap.securityScan?.results || [];
  const description = `This snapshot is infected with ransomware.${evidences.length ? ' Suspicious indicators found:' : ''}`;

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: '240px',
          },
        },
      }}
      title={
        <Stack padding='4px' gap='12px'>
          <Typography color='var(--mui-palette-customColors-tooltipText)'>
            {description}
          </Typography>
          <Stack gap='4px'>
            {evidences.map((evidence) => (
              <Stack
                key={evidence.evidenceType}
                direction='row'
                justifyContent='space-between'
              >
                <Typography color='var(--mui-palette-customColors-tooltipText)'>
                  {getEvidenceStrings(evidence.evidenceType).label}
                </Typography>
                <Typography color='var(--mui-palette-customColors-tooltipText)'>
                  {evidence.count}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      }
    >
      <span>
        <SkullIcon />
      </span>
    </Tooltip>
  );
};
